import { useEffect, useState, useCallback } from 'react';
import { api } from '../../api';
import { useApp } from '../../contexts/AppContext';

// Bridge between dispatch click and the backend's active_jobs_by_stage
// catching up. Without this, a user who navigates away & back within 20s
// of clicking Apply sees the callout again (stale dashboard seed + the
// next /stages/summary poll hasn't fired yet) and is tempted to click
// Apply a second time.
const DISPATCH_KEY = (caseId) => `pendingCallout:dispatched:${caseId}`;
const SUPPRESSION_MS = 2 * 60 * 1000;

function readRecentDispatch(caseId) {
  try {
    const raw = sessionStorage.getItem(DISPATCH_KEY(caseId));
    if (!raw) return new Set();
    const { ts, stages } = JSON.parse(raw);
    if (!ts || Date.now() - ts > SUPPRESSION_MS) {
      sessionStorage.removeItem(DISPATCH_KEY(caseId));
      return new Set();
    }
    return new Set(Array.isArray(stages) ? stages : []);
  } catch {
    return new Set();
  }
}

function markDispatched(caseId, stages) {
  try {
    sessionStorage.setItem(DISPATCH_KEY(caseId), JSON.stringify({
      ts: Date.now(), stages,
    }));
  } catch {
    // sessionStorage can throw in private mode; silently skip.
  }
}

/**
 * PendingUpdatesCallout — case-level "X docs have updates available" callout.
 *
 * Reads /cases/{caseId}/stages/summary on mount and whenever refreshKey
 * changes. Surfaces two buttons:
 *   - "Apply free updates"  — POSTs with classes=["derive"] (no consent)
 *   - "Apply all"           — POSTs with classes=["derive","analyze"]
 *                             after a window.confirm cost prompt
 *
 * Stays silent when:
 *   - nothing is pending
 *   - the user dismissed it this session
 *   - every pending stage is already covered by an active coordinated
 *     backfill job (server-side ``active_jobs_by_stage`` field). The
 *     BackgroundJobsBanner is already surfacing the work — re-asking
 *     "Apply?" would be a regression.
 *
 * Also polls the summary endpoint periodically so the suppression state
 * picks up coalesce-driven dispatches that happen after mount. Cadence
 * is 20s, not 5s: this is a "by the way" callout, not a live progress
 * widget — and the case page already has 4+ other pollers competing for
 * the backend DB pool. Polling pauses entirely when the tab is hidden.
 */
export default function PendingUpdatesCallout({ caseId, onDispatched, refreshKey = 0 }) {
  // Seed from the case-page mount dashboard envelope so we don't fire
  // a duplicate /stages/summary fetch immediately on first render.
  // Pool Governance P2: the dashboard endpoint already returned this
  // data inside one backend session.
  const { caseDashboard } = useApp();
  const [summary, setSummary] = useState(() => caseDashboard?.stages_summary || null);
  const [error, setError] = useState(null);
  const [dispatching, setDispatching] = useState(null);  // 'free' | 'paid' | null
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchSummary = () => {
      if (document.visibilityState !== 'visible') return;
      api.get(`/cases/${caseId}/stages/summary`)
        .then(d => { if (!cancelled) { setSummary(d); setError(null); } })
        .catch(err => { if (!cancelled) setError(err.message || 'failed'); });
    };
    // Skip the immediate fetch if the dashboard already seeded us;
    // the poll cadence will pick up freshness.
    if (!caseDashboard?.stages_summary || refreshKey > 0) {
      fetchSummary();
    }
    const t = setInterval(fetchSummary, 20000);
    return () => { cancelled = true; clearInterval(t); };
    // caseDashboard intentionally not in deps — we only use it for the
    // initial seed; subsequent updates come via the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, refreshKey]);

  // One stage on its own (its line's Apply): the backfill runs that stage and nothing else,
  // so a cheap, visible stage (document titles) never drags a heavy one (graph) along.
  const dispatchStage = useCallback(async (name, info) => {
    if (dispatching) return;
    const label = STAGE_COPY[name]?.label || prettify(name);
    const isFree = info.cost_class === 'derive';
    if (!isFree) {
      const ok = window.confirm(
        `${label}: apply to ${info.docs_missing} doc${info.docs_missing === 1 ? '' : 's'}?\n` +
        `Estimated cost: $${(info.cost_to_cure_cents / 100).toFixed(2)}`
      );
      if (!ok) return;
    }
    setDispatching(name);
    try {
      await api.post(`/cases/${caseId}/stages/backfill`, { classes: [info.cost_class], stage_filter: [name] });
      markDispatched(caseId, [name]);
      onDispatched?.();
      setDismissed(true);
    } catch (err) {
      setError(err.message || 'dispatch failed');
    } finally {
      setDispatching(null);
    }
  }, [caseId, dispatching, onDispatched]);

  const dispatchJob = useCallback(async (mode) => {
    if (dispatching) return;
    const classes = mode === 'free' ? ['derive'] : ['derive', 'analyze'];
    if (mode === 'paid' && summary?.paid_cost_cents > 0) {
      const dollars = (summary.paid_cost_cents / 100).toFixed(2);
      const docs = summary.paid_docs_to_cure;
      const ok = window.confirm(
        `Apply paid enrichment stages to ${docs} doc${docs === 1 ? '' : 's'}?\n` +
        `Estimated cost: $${dollars}\n\n` +
        `Free derive-class stages run alongside.`
      );
      if (!ok) return;
    }
    setDispatching(mode);
    try {
      await api.post(`/cases/${caseId}/stages/backfill`, { classes });
      // Persist a per-session marker so remounting (e.g. user navigates
      // to another tab and back) keeps the callout suppressed until the
      // backend's active_jobs_by_stage catches up.
      markDispatched(caseId, Object.keys(summary?.by_stage || {}));
      onDispatched?.();
      // Auto-dismiss after dispatch — the BackgroundJobsBanner will
      // surface progress, and re-rendering the callout while a job
      // runs would be noisy.
      setDismissed(true);
    } catch (err) {
      setError(err.message || 'dispatch failed');
    } finally {
      setDispatching(null);
    }
  }, [caseId, dispatching, summary, onDispatched]);

  if (dismissed) return null;
  if (error) return null;  // silent on read failure — don't break the inventory tab
  if (!summary) return null;
  if (summary.docs_with_pending <= 0) return null;

  const byStage = summary.by_stage || {};
  const activeByStage = summary.active_jobs_by_stage || {};
  const locallyDispatched = readRecentDispatch(caseId);

  // Strip stages whose updates are already being applied by a
  // coordinated job. If everything pending is covered, the BackgroundJobsBanner
  // is already telling the user what's happening — render nothing.
  // Also strip stages we just dispatched in this session, even if the
  // backend hasn't yet reported them as active (eventual-consistency gap
  // between job submit and the /stages/summary query reflecting it).
  const visibleByStage = Object.fromEntries(
    Object.entries(byStage).filter(
      ([name]) => !activeByStage[name] && !locallyDispatched.has(name)
    )
  );
  if (Object.keys(visibleByStage).length === 0) return null;

  const freeDocs = summary.free_docs_to_cure;
  const paidDocs = summary.paid_docs_to_cure;
  const paidCents = summary.paid_cost_cents || 0;

  return (
    <div className="inv-pending-callout">
      <div className="inv-pending-header">
        <span className="inv-pending-arrow">↑</span>
        <span className="inv-pending-title">
          New analysis available for {summary.docs_with_pending} document{summary.docs_with_pending === 1 ? '' : 's'}
        </span>
        <button
          className="inv-pending-dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
      <ul className="inv-pending-stages">
        {Object.entries(visibleByStage).map(([name, info]) => {
          const isFree = info.cost_class === 'derive';
          const cost = isFree ? 'free' : `$${(info.cost_to_cure_cents / 100).toFixed(2)}`;
          return (
            <li key={name} title={STAGE_COPY[name]?.desc} data-testid={`pending-stage-${name}`}>
              <span className="inv-pending-stage-name">{STAGE_COPY[name]?.label || prettify(name)}</span>
              <span className="inv-pending-stage-info">
                {info.docs_missing} doc{info.docs_missing === 1 ? '' : 's'} · {cost}
              </span>
              <button
                type="button"
                className="btn btn-sm inv-pending-stage-apply"
                disabled={dispatching != null}
                onClick={() => dispatchStage(name, info)}
                title={`Apply only "${STAGE_COPY[name]?.label || prettify(name)}"`}
              >
                {dispatching === name ? 'Dispatching…' : 'Apply'}
              </button>
            </li>
          );
        })}
      </ul>
      <div className="inv-pending-actions">
        {freeDocs > 0 && (
          <button
            className="btn btn-sm"
            disabled={dispatching != null}
            onClick={() => dispatchJob('free')}
          >
            {dispatching === 'free' ? 'Dispatching…' : `Apply free updates (${freeDocs} doc${freeDocs === 1 ? '' : 's'})`}
          </button>
        )}
        {paidDocs > 0 && (
          <button
            className="btn btn-sm btn-primary"
            disabled={dispatching != null}
            onClick={() => dispatchJob('paid')}
          >
            {dispatching === 'paid' ? 'Dispatching…' : `Apply all ($${(paidCents / 100).toFixed(2)})`}
          </button>
        )}
      </div>
    </div>
  );
}

// Outcome language for known pipeline stages — users are buying a result,
// not a stage name. Unknown stages fall back to prettify(name).
const STAGE_COPY = {
  document_title: {
    label: 'Document titles',
    desc: 'Names each document the way a lawyer files it: what it is, whose or about what, and when. About a cent a document.',
  },
  graph_extraction: {
    label: 'People, events & timeline links',
    desc: 'Re-reads these documents to update who appears, what happened, and the case timeline.',
  },
  entity_extraction: {
    label: 'Names & key details',
    desc: 'Finds people, places, and organizations mentioned in these documents.',
  },
  document_thumbnail: {
    label: 'Document previews',
    desc: 'Generates the preview images shown in the inventory.',
  },
  keyframe_thumbnails: {
    label: 'Video previews',
    desc: 'Generates preview frames for video files.',
  },
  segmentation: {
    label: 'Documents inside long PDFs',
    desc: 'Finds the separate documents inside PDFs of 20 pages or more and lists each one on its own.',
  },
};

function prettify(name) {
  return name
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
